import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Kpi, Loading } from "@/components/Widgets";
import { API, errMsg, pollJob } from "@/lib/api";
import { FrequencyPanel, FREQ_TYPES } from "@/components/calendar/FrequencyPanel";
import { CalendarGrid, iso, addDays, mondayOf } from "@/components/calendar/CalendarGrid";
import { ItemDialog, AddDialog } from "@/components/calendar/ItemDialog";


export default function Calendar() {
  const [start, setStart] = useState(() => mondayOf(new Date()));
  const [weeks, setWeeks] = useState(1);
  const [items, setItems] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(null);
  const [filling, setFilling] = useState(false);
  const [fillWeeks, setFillWeeks] = useState(4);
  const [replace, setReplace] = useState(true);
  const [newIdeas, setNewIdeas] = useState(true);
  const [fillChars, setFillChars] = useState(null);
  const end = addDays(start, weeks * 7 - 1);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/calendar`, { params: { start: iso(start), end: iso(end) } });
      setItems(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [start, weeks]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/calendar/prefs`).then((r) => setPrefs(r.data)); }, []);

  const patch = async (it, body) => {
    try {
      const r = await axios.patch(`${API}/calendar/${it.id}`, body);
      setItems((xs) => xs.map((x) => (x.id === it.id ? r.data : x)).filter((x) => x.date >= iso(start) && x.date <= iso(end)));
      return r.data;
    } catch (e) { toast.error(errMsg(e)); return null; }
  };
  const move = (id, date) => { const it = items.find((x) => x.id === id); if (it && it.date !== date) patch(it, { date }); };
  const toggle = (it) => patch(it, { status: it.status === "pubblicato" ? "pianificato" : "pubblicato" });
  const save = async (it, body) => { const r = await patch(it, body); if (r) { setOpen(body.status ? r : null); toast.success("Aggiornato"); } };
  const del = async (it) => { await axios.delete(`${API}/calendar/${it.id}`); setOpen(null); load(); };
  const create = async (body) => {
    try { await axios.post(`${API}/calendar`, body); setAdding(null); load(); toast.success("Contenuto aggiunto"); } catch (e) { toast.error(errMsg(e)); }
  };
  const savePrefs = async (user) => { const r = await axios.put(`${API}/calendar/prefs`, user); setPrefs(r.data); toast.success("Frequenza salvata"); };
  const fill = async (user, w, replace, dirty) => {
    setFilling(true);
    try {
      if (dirty) await savePrefs(user);
      const { data } = await axios.post(`${API}/calendar/fill`, { start: iso(start), weeks: w, replace, new_ideas: newIdeas });
      let res = data;
      if (data.job_id) {
        toast.info(`Le idee della strategia non bastano: l'AI sta creando ${data.needed} idee nuove...`);
        setFillChars(0);
        const rid = await pollJob(data.job_id, setFillChars);
        res = (await axios.get(`${API}/calendar/fill-result/${rid}`)).data;
      }
      toast.success(`${res.created} contenuti pianificati${res.new_ideas ? `, ${res.new_ideas} idee nuove create` : ""}${res.removed ? `, ${res.removed} sostituiti` : ""}`);
      if (res.reused) toast.warning(`${res.reused} slot usano varianti di idee esistenti`);
      if (w > 1 && weeks === 1) setWeeks(4); else load();
    } catch (e) { toast.error(errMsg(e)); } finally { setFilling(false); setFillChars(null); }
  };

  const stats = useMemo(() => {
    const pub = items.filter((i) => i.status === "pubblicato").length;
    const counts = {};
    items.forEach((i) => {
      const ig = i.platform.includes("Instagram"), fb = i.platform.includes("Facebook");
      const keys = [];
      if (ig && i.format === "Reel") keys.push("instagram_reel");
      if (ig && i.format === "Carosello") keys.push("instagram_carousel");
      if (ig && i.format === "Post") keys.push("instagram_post");
      if (fb && i.format === "Post") keys.push("facebook_post");
      keys.forEach((k) => { counts[k] = (counts[k] || 0) + 1; });
    });
    return { pub, total: items.length, counts };
  }, [items]);

  if (!prefs) return <Loading />;
  const target = (k) => (prefs.user[k] || 0) * weeks;
  return (
    <>
      <PageHeader eyebrow="Trascina i contenuti per spostarli · clic per dettagli e caption" title="Calendario editoriale" desc="Il piano si costruisce dalle idee della Strategia AI con la frequenza che scegli tu. Segna cosa hai pubblicato e tieni il ritmo.">
        <div className="flex border border-[#E8E8E4] bg-white">{[[1, "Settimana"], [4, "4 settimane"]].map(([w, l]) => <button key={w} data-testid={`view-${w}`} onClick={() => setWeeks(w)} className={`px-3 py-2 text-xs transition-colors ${weeks === w ? "bg-[#2A4B3C] text-white" : "hover:bg-[#F7F7F5]"}`}>{l}</button>)}</div>
        <div className="flex items-center gap-1">
          <Button data-testid="cal-prev" variant="outline" size="icon" className="rounded-full" onClick={() => setStart(addDays(start, -7 * weeks))}><ChevronLeft className="w-4 h-4" /></Button>
          <Button data-testid="cal-today" variant="outline" className="rounded-full" onClick={() => setStart(mondayOf(new Date()))}>Oggi</Button>
          <Button data-testid="cal-next" variant="outline" size="icon" className="rounded-full" onClick={() => setStart(addDays(start, 7 * weeks))}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </PageHeader>
      <FrequencyPanel prefs={prefs} onSave={savePrefs} onFill={fill} filling={filling} weeks={fillWeeks} setWeeks={setFillWeeks} replace={replace} setReplace={setReplace} newIdeas={newIdeas} setNewIdeas={setNewIdeas} fillChars={fillChars} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
        <Kpi testid="cal-kpi-published" label="Pubblicati" value={`${stats.pub}/${stats.total}`} hint={`${start.toLocaleDateString("it-IT", { day: "numeric", month: "short" })} – ${end.toLocaleDateString("it-IT", { day: "numeric", month: "short" })}`} accent />
        {FREQ_TYPES.map(([k, l]) => <Kpi key={k} testid={`cal-kpi-${k}`} label={l} value={`${stats.counts[k] || 0}/${target(k)}`} hint="pianificati / obiettivo" />)}
      </div>
      <CalendarGrid start={start} weeks={weeks} items={items} onMove={move} onOpen={setOpen} onToggle={toggle} onAdd={setAdding} />
      <ItemDialog item={open} onClose={() => setOpen(null)} onSave={save} onDelete={del} onUpdated={(it) => { setOpen(it); setItems((xs) => xs.map((x) => (x.id === it.id ? it : x))); }} />
      <AddDialog date={adding} onClose={() => setAdding(null)} onCreate={create} />
    </>
  );
}
